import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const MegaMenuWrapper = styled.div`
  position: absolute;
  top: calc(100% - 20px);
  left: 0;
  width: 100%;
  background: transparent;
  z-index: 98;
  opacity: ${(props) => (props.isActive ? 1 : 0)};
  visibility: ${(props) => (props.isActive ? "visible" : "hidden")};
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease;

  ${(props) =>
    props.isActive &&
    `
    top: 100%;
  `}
`;

const MegaMenuContent = styled.div`
  background: white;
  padding: 57px 0 53px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    right: 100%;
    top: 0;
    width: 100vw;
    height: 100%;
    background: white;
    z-index: -1;
  }

  &::after {
    content: "";
    position: absolute;
    left: 100%;
    top: 0;
    width: 100vw;
    height: 100%;
    background: white;
    z-index: -1;
  }
`;

const MenuContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const MenuGrid = styled.div`
  display: flex;
  gap: 40px;
  justify-content: space-between;
  text-align: left;
`;

const CategoriesSection = styled.div`
  display: flex;
  gap: 10px;
`;

const CategoryColumn = styled.div`
  width: 100%;
`;

const CategoryTitle = styled.h3`
  font-size: 14px;
  font-weight: bold;
  color: #333333bb;
  margin-bottom: 16px;
`;

const CategoryList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-auto-rows: min-content;
  gap: 10px 40px;
  padding-left: 0;
  list-style: none;
`;

const CategoryItem = styled.li`
  margin-bottom: 0px;
  width: 180px;
`;

const CategoryLink = styled(Link)`
  font-size: 14px;
  font-weight: bold;
  color: #000000ff;
  text-decoration: none;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 0;
    height: 2px;
    background-color: #00000085;
    transition: width 0.3s ease-in-out;
  }

  &:hover::after {
    width: 100%;
  }
`;

const ShopAllButton = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  padding: 12px 16px;
  border-radius: 25px;
  text-decoration: none;
  font-weight: bold;
  font-size: 14px;
  margin-top: 10px;
  min-width: 174px;
  text-align: center;

  &:hover {
    background: #e55a1c;
  }
`;

const FeaturedSection = styled.div`
  flex: 1;
  display: flex;
  gap: 40px;
  justify-content: flex-end;
`;

const FeaturedCard = styled.div`
  width: 100%;
  max-width: 416px;
`;

const FeaturedImage = styled.div`
  width: 100%;
  aspect-ratio: 416/312;
  border-radius: 0 0 43px 0;
  overflow: hidden;
  margin-bottom: 16px;
  background: #f5f5f5;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (min-width: 1280px) {
    border-radius: 0 0 90px 0;
  }
`;

const FeaturedContent = styled.div``;

const FeaturedTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 5px;
  color: #333;
`;

const FeaturedDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
`;

const FeaturedLink = styled(Link)`
  color: #333;
  text-decoration: underline;
  font-size: 14px;

  &:hover {
    color: #ff6720;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100vh - var(--visible-header-height, 154px));
  background: rgba(0, 0, 0, 0.2);
  z-index: -2;
  pointer-events: none;
`;

const menuData = {
  chainsaws: {
    categories: [
      { name: "Petrol Chainsaws", path: "/collections/chainsaws?type=petrol" },
      { name: "Electric Chainsaws", path: "/collections/chainsaws?type=corded" },
      { name: "Battery Chainsaws", path: "/collections/chainsaws?type=cordless" },
      { name: "Pole Saws", path: "/collections/pole-saws" },
      { name: "Top Handle Saws", path: "/collections/top-handle-saws" },
      { name: "Chainsaw Chains", path: "/collections/chainsaw-chains" },
      { name: "Chainsaw Bars", path: "/collections/chainsaw-bars" },
      { name: "Accessories", path: "/collections/chainsaw-accessories" },
    ],
    featured: [
      {
        title: "Professional Petrol Chainsaw",
        description: "Heavy-duty performance for demanding tasks",
        link: "/products/petrol-chainsaw-pro",
        linkText: "Learn More",
        image:
          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=416&h=312&fit=crop",
      },
      {
        title: "Cordless Power & Convenience",
        description: "Battery-powered chainsaws for every job",
        link: "/collections/cordless-chainsaws",
        linkText: "Explore Range",
        image:
          "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=416&h=312&fit=crop",
      },
    ],
  },
  brushcutters: {
    categories: [
      { name: "Petrol Brushcutters", path: "/collections/petrol-brushcutters" },
      {
        name: "Electric Brushcutters",
        path: "/collections/electric-brushcutters",
      },
      {
        name: "Battery Brushcutters",
        path: "/collections/battery-brushcutters",
      },
      {
        name: "Professional Models",
        path: "/collections/professional-brushcutters",
      },
      { name: "Bike Handle", path: "/collections/bike-handle-brushcutters" },
      { name: "Loop Handle", path: "/collections/loop-handle-brushcutters" },
      { name: "Cutting Lines", path: "/collections/cutting-lines" },
      { name: "Metal Blades", path: "/collections/brushcutter-blades" },
    ],
    featured: [
      {
        title: "Heavy-Duty Petrol Brushcutter",
        description: "Professional performance for tough vegetation",
        link: "/products/petrol-brushcutter-52cc",
        linkText: "Learn More",
        image:
          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=416&h=312&fit=crop",
      },
      {
        title: "Cordless Convenience",
        description: "Battery-powered trimmers for easy handling",
        link: "/collections/battery-brushcutters",
        linkText: "Discover Now",
        image:
          "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=416&h=312&fit=crop",
      },
    ],
  },
  blowers: {
    categories: [
      { name: "Petrol Blowers", path: "/collections/petrol-blowers" },
      { name: "Electric Blowers", path: "/collections/electric-blowers" },
      { name: "Battery Blowers", path: "/collections/battery-blowers" },
      { name: "Backpack Blowers", path: "/collections/backpack-blowers" },
      { name: "Handheld Blowers", path: "/collections/handheld-blowers" },
      {
        name: "Professional Models",
        path: "/collections/professional-blowers",
      },
      { name: "Vacuum Attachments", path: "/collections/blower-vacuum-kits" },
      { name: "Collection Bags", path: "/collections/collection-bags" },
      { name: "Nozzles & Tubes", path: "/collections/blower-nozzles" },
      { name: "Accessories", path: "/collections/blower-accessories" },
    ],
    featured: [
      {
        title: "Professional Backpack Blower",
        description: "Maximum power for large area clearing",
        link: "/products/backpack-blower-pro",
        linkText: "Learn More",
        image:
          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=416&h=312&fit=crop",
      },
      {
        title: "Lightweight & Powerful",
        description: "Cordless blowers for easy maneuverability",
        link: "/collections/battery-blowers",
        linkText: "Shop Now",
        image:
          "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=416&h=312&fit=crop",
      },
    ],
  },
  "pressure-washers": {
    categories: [
      {
        name: "Petrol Pressure Washers",
        path: "/collections/petrol-pressure-washers",
      },
      {
        name: "Electric Pressure Washers",
        path: "/collections/electric-pressure-washers",
      },
      {
        name: "Battery Pressure Washers",
        path: "/collections/battery-pressure-washers",
      },
      {
        name: "Professional Models",
        path: "/collections/professional-pressure-washers",
      },
      {
        name: "Hot Water Models",
        path: "/collections/hot-water-pressure-washers",
      },
      { name: "Surface Cleaners", path: "/collections/surface-cleaners" },
      { name: "Pressure Hoses", path: "/collections/pressure-hoses" },
      { name: "Nozzles & Tips", path: "/collections/pressure-nozzles" },
      { name: "Detergents", path: "/collections/pressure-detergents" },
      { name: "Accessories", path: "/collections/pressure-washer-accessories" },
    ],
    featured: [
      {
        title: "Professional Petrol Pressure Washer",
        description: "3000 PSI power for heavy-duty cleaning",
        link: "/products/petrol-pressure-washer-3000psi",
        linkText: "Learn More",
        image:
          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=416&h=312&fit=crop",
      },
      {
        title: "Electric Power & Efficiency",
        description: "Clean performance for home and business",
        link: "/collections/electric-pressure-washers",
        linkText: "Explore Range",
        image:
          "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=416&h=312&fit=crop",
      },
    ],
  },
  "lawn-mowers": {
    categories: [
      { name: "Petrol Lawn Mowers", path: "/collections/petrol-lawn-mowers" },
      {
        name: "Electric Lawn Mowers",
        path: "/collections/electric-lawn-mowers",
      },
      { name: "Battery Lawn Mowers", path: "/collections/battery-lawn-mowers" },
      {
        name: "Self-Propelled Mowers",
        path: "/collections/self-propelled-mowers",
      },
      { name: "Push Mowers", path: "/collections/push-mowers" },
      { name: "Ride-On Mowers", path: "/collections/ride-on-mowers" },
      { name: "Robotic Mowers", path: "/collections/robotic-mowers" },
      { name: "Mower Blades", path: "/collections/mower-blades" },
      { name: "Grass Catchers", path: "/collections/grass-catchers" },
      { name: "Accessories", path: "/collections/mower-accessories" },
    ],
    featured: [
      {
        title: "Self-Propelled Petrol Mower",
        description: "Effortless cutting with professional results",
        link: "/products/self-propelled-petrol-mower",
        linkText: "Learn More",
        image:
          "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=416&h=312&fit=crop",
      },
      {
        title: "Cordless Freedom",
        description: "Battery-powered mowing without limits",
        link: "/collections/battery-lawn-mowers",
        linkText: "Discover Now",
        image:
          "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=416&h=312&fit=crop",
      },
    ],
  },
};

const MegaMenu = ({ activeMenu, onClose, onMouseEnter, onMouseLeave }) => {
  if (!activeMenu || !menuData[activeMenu]) {
    return null;
  }

  const menu = menuData[activeMenu];

  return (
    <MegaMenuWrapper
      isActive={!!activeMenu}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <MegaMenuContent>
        <MenuContainer>
          <MenuGrid>
            <CategoriesSection>
              <CategoryColumn>
                <CategoryTitle>Categories</CategoryTitle>
                <CategoryList>
                  {menu.categories.map((category, index) => (
                    <CategoryItem key={index}>
                      <CategoryLink to={category.path} onClick={onClose}>
                        {category.name}
                      </CategoryLink>
                    </CategoryItem>
                  ))}
                </CategoryList>
                <ShopAllButton
                  to={`/collections/${activeMenu}`}
                  onClick={onClose}
                >
                  Shop All{" "}
                  {activeMenu === "pressure-washers"
                    ? "Pressure Washers"
                    : activeMenu === "lawn-mowers"
                      ? "Lawn Mowers"
                      : activeMenu.charAt(0).toUpperCase() +
                        activeMenu.slice(1)}
                </ShopAllButton>
              </CategoryColumn>
            </CategoriesSection>

            <FeaturedSection>
              {menu.featured.map((item, index) => (
                <FeaturedCard key={index}>
                  <FeaturedImage>
                    <img src={item.image} alt={item.title} />
                  </FeaturedImage>
                  <FeaturedContent>
                    <FeaturedTitle>{item.title}</FeaturedTitle>
                    <FeaturedDescription>
                      {item.description}
                    </FeaturedDescription>
                    <FeaturedLink to={item.link} onClick={onClose}>
                      {item.linkText}
                    </FeaturedLink>
                  </FeaturedContent>
                </FeaturedCard>
              ))}
            </FeaturedSection>
          </MenuGrid>
        </MenuContainer>
      </MegaMenuContent>
      <Overlay />
    </MegaMenuWrapper>
  );
};

export default MegaMenu;
